import type { OutputsRequestParams, PaginatedRequestParams } from '../api/nm-types'
import type { Input, LocalBackupType, RegionSortableField } from 'common/api/v1/types'

/** All domains must have an `all` property with the base key */
type QueryKeyDomain = {
  all: readonly [string]
}

const inputBase = 'inputs' as const
const input = {
  all: [inputBase] as const,
  get: (id: Input['id']) => [inputBase, id] as const,
}

const outputBase = 'outputs' as const
const output = {
  all: [outputBase] as const,
  withLists: (params: OutputsRequestParams) => [outputBase, 'withLists', params] as const,
}

const regionBase = 'regions' as const
const region = {
  all: [regionBase] as const,
  lists: () => [regionBase, 'list'] as const,
  list: (params: PaginatedRequestParams<RegionSortableField>) => [regionBase, 'list', params] as const,
  details: () => [regionBase, 'detail'] as const,
  detail: (id: string) => [regionBase, 'detail', id] as const,
  scales: () => [regionBase, 'scale'] as const,
  scale: (id: string) => [regionBase, 'scale', id] as const,
}

const settingsBase = 'settings' as const
const settings = {
  all: [settingsBase] as const,
}

const backupBase = 'backups' as const
const backup = {
  all: [backupBase] as const,
  local: () => [backupBase, 'local'] as const,
  localByType: (type: LocalBackupType) => [backupBase, 'local', type] as const,
  externalS3: () => [backupBase, 'externalS3'] as const,
  externalS3PullStatus: () => [backupBase, 'externalS3', 'pullStatus'] as const,
  restoreStatus: () => [backupBase, 'restoreStatus'] as const,
}

const metricsBase = 'metrics' as const
/**
 * The parts of a metrics-series key that identify the stream. Grouped into one object so placeholder data can
 * carry over on a column/range change but never across streams, without indexing into the key tuple by position.
 */
export interface MetricsSeriesIdentity {
  scope: 'input' | 'output'
  scopeId: string
  table: string
  streamId?: string
  channelId?: string
  applianceId?: string
}
const metrics = {
  all: [metricsBase] as const,
  series: (identity: MetricsSeriesIdentity, columns: string, from: number, to: number) =>
    [metricsBase, 'series', identity, columns, from, to] as const,
}

const recoveryBase = 'recovery' as const
const recovery = {
  all: [recoveryBase] as const,
  config: () => [recoveryBase, 'config'] as const,
}

/**
 * Centralized query keys for TanStack Query.
 * Each domain's `all[0]` is the base key - must be unique across domains.
 */
export const keys = {
  input,
  output,
  region,
  settings,
  backup,
  metrics,
  recovery,
} satisfies Record<string, QueryKeyDomain>
